import {
  Box,
  Spinner,
  WizardStep,
  WizardStepRenderParams,
  useEffect,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaLoginStepDocument, A3innuvaTokenStatusFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Status } from 'modules/payroll_integrations_a3innuva/lib/OauthService'
import { A3InnuvaWizardData } from 'modules/payroll_integrations_a3innuva/lib/types'

import { NotAuthenticated, NotLoggedIn } from './Authentication'
import { Header } from './Header'
import { Hints } from './Hints'
import { Logout } from './Logout'
import { OrganizationalUnits } from './OrganizationalUnits'

const findCurrentToken = (
  statuses: A3innuvaTokenStatusFragment[],
  organizationalUnitId: string | null | undefined
) => {
  if (!organizationalUnitId) {
    // When there is no configuration we don't have any organizational unit ID assigned.
    // In this case we return the first token.
    // It should be the unique token for the company as we don't allow creating more than a token.
    return statuses[0]
  }

  const tokenStatus = statuses.find((token) => token.organizationalUnitId === organizationalUnitId)

  return tokenStatus
}

const LoginStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()
  const { data: loginStepData, loading } = useQuery(A3InnuvaLoginStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
    },
  })

  const tokenStatuses = loginStepData?.payrollIntegrationsA3innuva.oauthTokensConnection.nodes || []
  const organizationalUnitId = data.answers.organizational_unit_id
  const currentToken = findCurrentToken(tokenStatuses, organizationalUnitId)
  const loginStatus = currentToken?.status
  const isAuthenticated = loginStatus === Status.Authenticated

  useEffect(() => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
      },
      loginStatus: isAuthenticated,
    })
  }, [isAuthenticated])

  useOnNext(() => {
    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsClientIdSelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      client_id: data.answers.organizational_unit_id,
    })
  })

  return loading ? (
    <Spinner />
  ) : (
    <>
      <Box flexDirection="row">
        <Box gap="s24">
          <Header isLoggedIn={!!currentToken && isAuthenticated} />
          <Box justifyContent="spaceBetween" flexDirection="column" gap="s24">
            {!currentToken || !isAuthenticated ? (
              <NotLoggedIn companyId={session.company.id} accessId={session.access.id} />
            ) : (
              <Box maxWidth="s600" flexDirection="column" gap="s24" paddingRight="s24">
                {currentToken && (
                  <Logout
                    owner={currentToken.owner}
                    createdAt={currentToken.createdAt}
                    organizationalUnitId={currentToken.organizationalUnitId}
                  />
                )}
                <OrganizationalUnits data={data} onDataChanged={onDataChanged} />
                {!isAuthenticated && organizationalUnitId && (
                  <NotAuthenticated
                    companyId={session.company.id}
                    organizationalUnitId={organizationalUnitId}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box width="s400" padding="s24">
          <Hints />
        </Box>
      </Box>
    </>
  )
}

export const loginStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.login.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <LoginStep {...props} />,
  isCompleted: (data) => !!data.answers.organizational_unit_id && data.loginStatus === true,
})
